import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import {
  AnchoredDetailCalloutBody,
  AnchoredDetailCalloutFieldLabel,
  AnchoredDetailCalloutFooter,
  AnchoredDetailCalloutTitle,
  DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_MIN_HEIGHT_PX,
  DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_WIDTH_PX,
  FormAnchoredDetailCallout,
} from "@/components/ui/AnchoredDetailFormCallout";
import { buildAttributeAnchorSelector } from "@/components/ui/AnchoredDetailCallout";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { MANUFACTURING_HUB_READ_ONLY } from "@/pages/manufacturing/workOrdersPageModel";

export const PRODUCTION_CALENDAR_BATCH_ANCHOR_ATTRIBUTE = "data-production-calendar-batch";
export const BATCH_ON_HOLD_CONTROLS_ANCHOR_ATTRIBUTE = "data-batch-on-hold-controls";

export function productionCalendarBatchAnchorKey(batchId: number): string {
  return String(batchId);
}

export function productionCalendarBatchAnchorSelector(batchId: number): string {
  return buildAttributeAnchorSelector(
    PRODUCTION_CALENDAR_BATCH_ANCHOR_ATTRIBUTE,
    productionCalendarBatchAnchorKey(batchId),
  );
}

export function buildBatchOnHoldAnchorSelector(batchId: number): string {
  return buildAttributeAnchorSelector(BATCH_ON_HOLD_CONTROLS_ANCHOR_ATTRIBUTE, String(batchId));
}

export interface BatchOnHoldReasonCalloutProps {
  batchId: number;
  shipmentOnHold: boolean;
  hubWritesAllowed: boolean;
  anchorSelector?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function BatchOnHoldReasonCallout({
  batchId,
  shipmentOnHold,
  hubWritesAllowed,
  anchorSelector,
  onClose,
  onSuccess,
}: BatchOnHoldReasonCalloutProps): JSX.Element {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");

  useEffect(() => {
    setReason("");
  }, [batchId, shipmentOnHold]);

  const mutation = useMutation({
    mutationFn: async (noteReason: string) =>
      apiFetch<{ batchId: number; shipmentOnHold: boolean }>(
        `/api/manufacturing/production-calendar/batch/${String(batchId)}/shipment-hold`,
        {
          method: "POST",
          body: { shipmentOnHold, reason: noteReason },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "production-calendar"], exact: false });
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "batch-details"], exact: false });
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "batch-team-notes", batchId] });
      onSuccess?.();
      onClose();
    },
  });

  const title = shipmentOnHold
    ? `Place batch ${String(batchId)} on shipment hold`
    : `Release batch ${String(batchId)} from shipment hold`;
  const actionLabel = shipmentOnHold ? "Place shipment hold" : "Release shipment hold";
  const placeholder = shipmentOnHold
    ? "Why hold shipment for this batch? Manufacturing continues; pre-pack and outbound hide it until released."
    : "Why release shipment hold for this batch?";

  const resolvedAnchorSelector = anchorSelector ?? productionCalendarBatchAnchorSelector(batchId);

  return (
    <FormAnchoredDetailCallout
      anchorSelector={resolvedAnchorSelector}
      onClose={onClose}
      panelWidth={DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_WIDTH_PX}
      minContentHeight={DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_MIN_HEIGHT_PX}
      ariaLabel={title}
      measureKey={`${String(batchId)}-${shipmentOnHold ? "hold" : "release"}`}
    >
      <AnchoredDetailCalloutBody>
        <AnchoredDetailCalloutTitle>{title}</AnchoredDetailCalloutTitle>
        <AnchoredDetailCalloutFieldLabel label="Reason">
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            disabled={!hubWritesAllowed || mutation.isPending}
            rows={4}
            className="w-full resize-y rounded-md border border-slate-300 px-2.5 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
            placeholder={hubWritesAllowed ? placeholder : "You do not have permission to change shipment hold."}
          />
        </AnchoredDetailCalloutFieldLabel>
        {mutation.isError ? (
          <p className="text-xs text-red-700">
            {mutation.error instanceof ApiError ? mutation.error.message : "Could not update shipment hold."}
          </p>
        ) : null}
      </AnchoredDetailCalloutBody>
      <AnchoredDetailCalloutFooter>
        <Button type="button" variant="secondary" size="sm" disabled={mutation.isPending} onClick={onClose}>
          Cancel
        </Button>
        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} className="inline-block">
          <Button
            type="button"
            size="sm"
            disabled={!hubWritesAllowed || reason.trim() === "" || mutation.isPending}
            onClick={() => mutation.mutate(reason.trim())}
          >
            {mutation.isPending ? "Saving…" : actionLabel}
          </Button>
        </ReadOnlyHoverLayer>
      </AnchoredDetailCalloutFooter>
    </FormAnchoredDetailCallout>
  );
}
